import React, { useRef, useEffect } from 'react';
import { Card } from "react-bootstrap";
import styles from '../SessionHistoryDetailView.module.css';

const ClientProgressSection = ({
  sessionFormData,
  updateFormData,
  clientResponseToCareError,
  progressTowardGoalsError,
  barriersToProgressError,
  setClientResponseToCareError,
  setProgressTowardGoalsError,
  setBarriersToProgressError,
  autoResizeTextarea,
  onAutoSave
}) => {
  const autoSaveTimeoutRef = useRef(null);
  const autoSaveIntervalRef = useRef(null);

  const progressTowardGoalsOptions = [
    "Improved",
    "No Change",
    "Regressed",
    "Partially Achieved",
    "Goal Met"
  ];

  const barriersToProgressOptions = [
    "Ongoing stressors",
    "Limited support",
    "Non-adherence",
    "Health / medication issues",
    "Financial constraints",
    "Lack of motivation",
    "Other"
  ];

  const handleProgressChange = (option) => {
    const currentProgress = sessionFormData.progressTowardGoals || [];
    if (currentProgress.includes(option)) {
      // Remove option
      const updatedProgress = currentProgress.filter((p) => p !== option);
      updateFormData("progressTowardGoals", updatedProgress);
      if (option === "Goal Met") {
        updateFormData("goalMetDescription", "");
      }
    } else {
      // Add option
      updateFormData("progressTowardGoals", [...currentProgress, option]);
    }
    // Clear error when progress is selected
    if (progressTowardGoalsError) {
      setProgressTowardGoalsError("");
    }
    // Trigger auto-save
    if (onAutoSave) {
      onAutoSave();
    }
  };

  const handleBarrierChange = (barrier) => {
    const currentBarriers = sessionFormData.barriersToProgress || [];
    if (currentBarriers.includes(barrier)) {
      // Remove barrier
      const updatedBarriers = currentBarriers.filter((b) => b !== barrier);
      updateFormData("barriersToProgress", updatedBarriers);
      if (barrier === "Other") {
        updateFormData("otherBarrierText", "");
      }
    } else {
      // Add barrier
      updateFormData("barriersToProgress", [...currentBarriers, barrier]);
    }
    // Trigger auto-save
    if (onAutoSave) {
      onAutoSave();
    }
  };

  const handleBlur = () => {
    // Clear any pending auto-save from onChange
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
    // Trigger auto-save immediately on blur
    if (onAutoSave) {
      onAutoSave();
    }
  };

  // Set up 30-second interval auto-save while editing
  useEffect(() => {
    if (onAutoSave) {
      autoSaveIntervalRef.current = setInterval(() => {
        onAutoSave();
      }, 30000); // 30 seconds
    }

    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [onAutoSave]);

  return (
    <Card id="client-progress" className={styles.card}>
      <h3 className={styles.sectionHeading}>
        Client Progress
      </h3>

      {/* Client Response to Care */}
      <div style={{ marginBottom: "24px" }}>
        <label className={styles.labelWithMargin}>
          Client response to care.
        </label>
        <textarea
          value={sessionFormData.clientResponseToCare || ""}
          onChange={(e) => {
            const value = e.target.value;
            updateFormData("clientResponseToCare", value);
            if (clientResponseToCareError && value.trim()) {
              setClientResponseToCareError("");
            }
            // Debounced auto-save on change (1 second delay)
            if (autoSaveTimeoutRef.current) {
              clearTimeout(autoSaveTimeoutRef.current);
            }
            if (onAutoSave) {
              autoSaveTimeoutRef.current = setTimeout(() => {
                onAutoSave();
                autoSaveTimeoutRef.current = null;
              }, 1000);
            }
          }}
          onBlur={handleBlur}
          placeholder="Free-text"
          className={styles.textarea}
          required
        />
        {clientResponseToCareError && (
          <div className={styles.errorMessage} style={{ marginTop: "4px", marginBottom: "8px" }}>
            {clientResponseToCareError}
          </div>
        )}
      </div>

      <div className={styles.separator}></div>

      {/* Progress Toward Goals */}
      <div style={{ marginBottom: "24px" }}>
        <label className={styles.labelWithMarginLarge}>
          Progress toward goals
        </label>
        {progressTowardGoalsError && (
          <div className={styles.errorMessage} style={{ marginTop: "8px", marginBottom: "12px" }}>
            {progressTowardGoalsError}
          </div>
        )}
        <div className={styles.checkboxRow}>
          <div className={styles.checkboxColumn}>
            {progressTowardGoalsOptions.map((option) => (
              <div key={option} className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id={`progress-${option}`}
                  checked={sessionFormData.progressTowardGoals?.includes(option) || false}
                  onChange={() => handleProgressChange(option)}
                  className={styles.checkbox}
                />
                <label htmlFor={`progress-${option}`} className={styles.checkboxLabel}>
                  {option}
                </label>
              </div>
            ))}
          </div>
        </div>
        {sessionFormData.progressTowardGoals?.includes("Goal Met") && (
          <div style={{ marginTop: "12px" }}>
            <textarea
              id="goal-met-description-textarea"
              value={sessionFormData.goalMetDescription || ""}
              onChange={(e) => {
                autoResizeTextarea(e.target);
                const value = e.target.value;
                updateFormData("goalMetDescription", value);
                // Debounced auto-save
                if (autoSaveTimeoutRef.current) {
                  clearTimeout(autoSaveTimeoutRef.current);
                }
                if (onAutoSave) {
                  autoSaveTimeoutRef.current = setTimeout(() => {
                    onAutoSave();
                    autoSaveTimeoutRef.current = null;
                  }, 1000);
                }
              }}
              onBlur={handleBlur}
              placeholder="Describe achieved goal."
              className={styles.otherTextInput}
              rows={1}
              style={{ minHeight: "34px", resize: "none", overflow: "hidden" }}
            />
          </div>
        )}
      </div>

      <div className={styles.separator}></div>

      {/* Barriers to Progress */}
      <div style={{ marginBottom: "24px" }}>
        <label className={styles.labelWithMarginLarge}>
          Barriers to Progress
        </label>
        {barriersToProgressError && (
          <div className={styles.errorMessage} style={{ marginTop: "8px", marginBottom: "12px" }}>
            {barriersToProgressError}
          </div>
        )}
        <div className={styles.checkboxRow}>
          <div className={styles.checkboxColumn}>
            {barriersToProgressOptions.map((barrier) => (
              <div key={barrier} className={styles.checkboxContainer}>
                <input
                  type="checkbox"
                  id={`barrier-${barrier}`}
                  checked={sessionFormData.barriersToProgress?.includes(barrier) || false}
                  onChange={() => handleBarrierChange(barrier)}
                  className={styles.checkbox}
                />
                <label htmlFor={`barrier-${barrier}`} className={styles.checkboxLabel}>
                  {barrier}
                </label>
              </div>
            ))}
          </div>
        </div>
        {sessionFormData.barriersToProgress?.includes("Other") && (
          <div style={{ marginTop: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", marginBottom: "4px" }}>
              <div style={{ fontSize: "12px", color: "#8E8E93", whiteSpace: "nowrap" }}>
                {sessionFormData.otherBarrierText?.length || 0}/100
              </div>
            </div>
            <textarea
              id="barrier-other-textarea"
              value={sessionFormData.otherBarrierText || ""}
              onChange={(e) => {
                autoResizeTextarea(e.target);
                const value = e.target.value;
                if (value.length <= 100) {
                  updateFormData("otherBarrierText", value);
                }
                // Debounced auto-save
                if (autoSaveTimeoutRef.current) {
                  clearTimeout(autoSaveTimeoutRef.current);
                }
                if (onAutoSave) {
                  autoSaveTimeoutRef.current = setTimeout(() => {
                    onAutoSave();
                    autoSaveTimeoutRef.current = null;
                  }, 1000);
                }
              }}
              onBlur={handleBlur}
              placeholder="Please specify."
              className={styles.otherTextInput}
              rows={1}
              style={{ minHeight: "34px", resize: "none", overflow: "hidden" }}
              maxLength={100}
            />
          </div>
        )}
      </div>

      <div className={styles.separator}></div>

      {/* Additional Notes */}
      <div style={{ marginBottom: "16px" }}>
        <label className={styles.labelWithMargin}>
          Additional Notes
        </label>
        <textarea
          value={sessionFormData.additionalNotes || ""}
          onChange={(e) => {
            const value = e.target.value;
            updateFormData("additionalNotes", value);
            // Debounced auto-save on change (1 second delay)
            if (autoSaveTimeoutRef.current) {
              clearTimeout(autoSaveTimeoutRef.current);
            }
            if (onAutoSave) {
              autoSaveTimeoutRef.current = setTimeout(() => {
                onAutoSave();
                autoSaveTimeoutRef.current = null;
              }, 1000);
            }
          }}
          onBlur={handleBlur}
          placeholder="Free-text"
          className={styles.textarea}
        />
      </div>
    </Card>
  );
};

export default ClientProgressSection;

